import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Building2,
  ExternalLink,
  MoreHorizontal,
  Plus,
  Search,
  UserCog,
  Trash2,
  Pencil,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu'
import { useNotify } from '@/components/shared/useNotify'
import { useFilterDefaultsFromPrefs } from '@/hooks/useFilterDefaultsFromPrefs'
import {
  VENDOR_KINDS,
  type AdminVendorListFilters,
  type AdminVendorListRow,
  type VendorKind,
  useAdminVendorsList,
  useSoftDeleteVendor,
  useMoveVendorToPerson,
} from '@/hooks/useAdminVendors'
import { AdminVendorEditDialog } from './AdminVendorEditDialog'

/**
 * Phase 2 vendor unification — admin Vendors list page.
 *
 * Plan: 2026-05-04-vendor-unification-phase2.md Task 21.
 * Hooks: src/hooks/useAdminVendors.ts (Task 23).
 * Dialog: ./AdminVendorEditDialog.tsx (Task 22).
 * New page: ./AdminVendorNewPage.tsx (Task 22).
 *
 * Filter rail mirrors the admin pattern (left sidebar) + uses
 * useFilterDefaultsFromPrefs('admin-vendors').
 */

const PAGE_SIZE = 50

interface VendorActionState {
  vendor: AdminVendorListRow
  kind: 'move' | 'delete'
}

type AdminVendorFilters = AdminVendorListFilters

export function AdminVendorsListPage() {
  const { defaults } = useFilterDefaultsFromPrefs<AdminVendorFilters>(
    'admin-vendors',
  )
  const [filters, setFilters] =
    // eslint-disable-next-line intelhub/filter-rail-allowlist-complete -- admin-only page, not in the rule-4 allowlist (which targets user-facing list pages); useFilterDefaultsFromPrefs is still adopted above.
    useState<AdminVendorFilters>(defaults ?? { page: 1, limit: PAGE_SIZE })

  // Hydrate filters once defaults arrive (defer-to-prefs initial render).
  useEffect(() => {
    if (defaults) setFilters((prev) => ({ ...defaults, ...prev }))
  }, [defaults])

  const listQuery = useAdminVendorsList(filters)
  const softDelete = useSoftDeleteVendor()
  const moveToPerson = useMoveVendorToPerson()
  const { notify } = useNotify()

  const [editVendorId, setEditVendorId] = useState<string | null>(null)
  const [pendingAction, setPendingAction] = useState<VendorActionState | null>(
    null,
  )

  const rows = listQuery.data?.rows ?? []
  const total = listQuery.data?.total ?? 0
  const page = filters.page ?? 1
  const limit = filters.limit ?? PAGE_SIZE
  const totalPages = Math.max(1, Math.ceil(total / limit))

  function patch(next: Partial<AdminVendorFilters>) {
    setFilters((prev) => ({ ...prev, ...next, page: 1 }))
  }

  async function confirmMove() {
    if (!pendingAction || pendingAction.kind !== 'move') return
    const v = pendingAction.vendor
    try {
      const result = await moveToPerson.mutateAsync({ id: v.id })
      if ('moved' in result) {
        notify({ title: `${v.name} moved to persons`, variant: 'success' })
      } else {
        notify({
          title: `${v.name} was already moved`,
          description: `Person ID: ${result.person_id}`,
          variant: 'default',
        })
      }
      setPendingAction(null)
    } catch (err) {
      // The api wrapper throws ApiError with the body as message; the body
      // contains the "vendor_concurrently_attached" string for the 409 case.
      const message = (err as Error).message
      if (message.includes('vendor_concurrently_attached')) {
        notify({
          title: 'Concurrent attach detected — please retry',
          variant: 'warning',
        })
      } else {
        notify({
          title: `Failed to move ${v.name}`,
          description: message,
          variant: 'error',
        })
      }
      setPendingAction(null)
    }
  }

  async function confirmDelete() {
    if (!pendingAction || pendingAction.kind !== 'delete') return
    const v = pendingAction.vendor
    try {
      await softDelete.mutateAsync(v.id)
      notify({ title: `${v.name} marked inactive`, variant: 'success' })
    } catch (err) {
      notify({
        title: `Failed to delete ${v.name}`,
        description: (err as Error).message,
        variant: 'error',
      })
    } finally {
      setPendingAction(null)
    }
  }

  const filterRail = useMemo(
    () => (
      <aside
        className="hidden lg:flex w-60 flex-col gap-4 p-4 border-r"
        aria-label="Vendor filters"
      >
        <h2 className="text-sm font-semibold">Filters</h2>

        <div className="space-y-1.5">
          <Label htmlFor="vendor-search">Search</Label>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              id="vendor-search"
              className="pl-8"
              placeholder="Name…"
              value={filters.q ?? ''}
              onChange={(e) => patch({ q: e.target.value || undefined })}
            />
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="vendor-kind-filter">Kind</Label>
          <Select
            value={filters.kind ?? 'all'}
            onValueChange={(v) =>
              patch({ kind: v === 'all' ? undefined : (v as VendorKind) })
            }
          >
            <SelectTrigger id="vendor-kind-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All kinds</SelectItem>
              {VENDOR_KINDS.map((k) => (
                <SelectItem key={k} value={k}>
                  {k}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="vendor-country-filter">Country (α2)</Label>
          <Input
            id="vendor-country-filter"
            placeholder="us"
            maxLength={2}
            value={filters.country ?? ''}
            onChange={(e) =>
              patch({ country: e.target.value.toLowerCase() || undefined })
            }
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="vendor-active-filter">Active</Label>
          <Select
            value={filters.isActive ?? 'all'}
            onValueChange={(v) =>
              patch({
                isActive:
                  v === 'all' ? undefined : (v as 'true' | 'false'),
              })
            }
          >
            <SelectTrigger id="vendor-active-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="true">Active</SelectItem>
              <SelectItem value="false">Inactive</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <Button
          variant="ghost"
          size="sm"
          onClick={() => setFilters({ page: 1, limit: PAGE_SIZE })}
        >
          Clear filters
        </Button>
      </aside>
    ),
    [filters],
  )

  return (
    <div className="flex flex-1 min-h-0">
      {filterRail}

      <div className="flex-1 flex flex-col min-w-0">
        <header className="flex items-center justify-between gap-3 px-4 md:px-6 py-4 border-b">
          <div className="flex items-center gap-3">
            <Building2 className="h-5 w-5 text-muted-foreground" />
            <div>
              <h1 className="text-xl font-semibold">Vendors</h1>
              <p className="text-xs text-muted-foreground">
                {listQuery.isLoading
                  ? 'Loading vendors…'
                  : `${total} vendor${total === 1 ? '' : 's'}`}
              </p>
            </div>
          </div>
          <Button asChild size="sm">
            <Link to="/admin/vendors/new">
              <Plus className="h-4 w-4 mr-2" />
              New Vendor
            </Link>
          </Button>
        </header>

        <div className="flex-1 overflow-auto p-4 md:p-6">
          <div className="overflow-x-auto rounded-md border">
            <table className="w-full text-sm">
              <thead className="bg-muted/40">
                <tr className="text-left text-xs uppercase text-muted-foreground">
                  <th className="px-3 py-2 font-medium">Name</th>
                  <th className="px-3 py-2 font-medium">Slug</th>
                  <th className="px-3 py-2 font-medium">Kind</th>
                  <th className="px-3 py-2 font-medium">Tags</th>
                  <th className="px-3 py-2 font-medium">Country</th>
                  <th className="px-3 py-2 font-medium">Active</th>
                  <th className="px-3 py-2 font-medium">Moved?</th>
                  <th className="px-3 py-2 font-medium">Homepage</th>
                  <th className="px-3 py-2 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && !listQuery.isLoading ? (
                  <tr>
                    <td
                      colSpan={9}
                      className="px-3 py-8 text-center text-muted-foreground"
                    >
                      No vendors match the current filters.
                    </td>
                  </tr>
                ) : (
                  rows.map((v) => (
                    <tr
                      key={v.id}
                      className="border-t hover:bg-muted/30"
                      data-testid={`vendor-row-${v.id}`}
                    >
                      <td className="px-3 py-2 font-medium">{v.name}</td>
                      <td className="px-3 py-2 text-xs text-muted-foreground tabular-nums">
                        {v.slug}
                      </td>
                      <td className="px-3 py-2">
                        <Badge variant="outline">{v.vendorKind}</Badge>
                      </td>
                      <td className="px-3 py-2 max-w-[200px]">
                        {(v.vendorTags ?? []).length === 0 ? (
                          <span className="text-xs italic text-muted-foreground">
                            —
                          </span>
                        ) : (
                          <div className="flex gap-1 flex-wrap">
                            {(v.vendorTags ?? []).slice(0, 3).map((t) => (
                              <Badge key={t} variant="secondary" className="text-[10px]">
                                {t}
                              </Badge>
                            ))}
                            {(v.vendorTags ?? []).length > 3 && (
                              <span className="text-xs text-muted-foreground">
                                +{(v.vendorTags ?? []).length - 3}
                              </span>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 uppercase text-xs">
                        {v.countryIso ?? '—'}
                      </td>
                      <td className="px-3 py-2">
                        {v.isActive ? (
                          <Badge variant="outline" className="bg-green-500/10 text-green-700">
                            yes
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-muted-foreground">
                            no
                          </Badge>
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs">
                        {v.movedToPersonId ? (
                          <Badge variant="outline">person</Badge>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs">
                        {v.homepageUrl ? (
                          <a
                            href={v.homepageUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-primary hover:underline"
                          >
                            {new URL(v.homepageUrl).host}
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7"
                              aria-label={`Actions for ${v.name}`}
                            >
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onSelect={() => setEditVendorId(v.id)}
                            >
                              <Pencil className="h-3.5 w-3.5" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onSelect={() =>
                                setPendingAction({ vendor: v, kind: 'move' })
                              }
                              disabled={!!v.movedToPersonId}
                            >
                              <UserCog className="h-3.5 w-3.5" />
                              Move to Persons
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              variant="destructive"
                              onSelect={() =>
                                setPendingAction({ vendor: v, kind: 'delete' })
                              }
                              disabled={!v.isActive}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                              Soft-delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="mt-4 flex items-center justify-between text-xs text-muted-foreground">
            <span>
              Page {page} of {totalPages}
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() =>
                  setFilters((prev) => ({ ...prev, page: Math.max(1, page - 1) }))
                }
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages}
                onClick={() =>
                  setFilters((prev) => ({
                    ...prev,
                    page: Math.min(totalPages, page + 1),
                  }))
                }
              >
                Next
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Edit dialog */}
      <AdminVendorEditDialog
        vendorId={editVendorId}
        open={editVendorId !== null}
        onOpenChange={(open) => {
          if (!open) setEditVendorId(null)
        }}
      />

      {/* Move-to-Persons confirm */}
      <Dialog
        open={pendingAction?.kind === 'move'}
        onOpenChange={(open) => {
          if (!open) setPendingAction(null)
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Move to Persons</DialogTitle>
            <DialogDescription>
              {pendingAction?.kind === 'move' && (
                <>
                  This converts <strong>{pendingAction.vendor.name}</strong> to a
                  person record. Stories + vulnerabilities currently referencing
                  this vendor will have the reference updated. Reversible only via
                  SQL.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setPendingAction(null)}
              disabled={moveToPerson.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={() => void confirmMove()}
              disabled={moveToPerson.isPending}
            >
              {moveToPerson.isPending ? 'Moving…' : 'Move to Persons'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Soft-delete confirm */}
      <Dialog
        open={pendingAction?.kind === 'delete'}
        onOpenChange={(open) => {
          if (!open) setPendingAction(null)
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mark vendor inactive?</DialogTitle>
            <DialogDescription>
              {pendingAction?.kind === 'delete' && (
                <>
                  Mark <strong>{pendingAction.vendor.name}</strong> inactive?
                  Stories + vulnerabilities continue to reference it but it won't
                  appear in lists. Reversible via 'show inactive' toggle.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setPendingAction(null)}
              disabled={softDelete.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => void confirmDelete()}
              disabled={softDelete.isPending}
            >
              {softDelete.isPending ? 'Deleting…' : 'Mark inactive'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
